<script setup lang="ts">
import { computed } from 'vue';
import { IconCheckCircleFilled } from '@/components/design/icons';
import type { VaultFileSignatureInfoInDb } from '@/api';
import GaiaSignatureList from '@/components/design/domain/fileVault/GaiaSignatureList.vue';
import GaiaStatusChip from '@/components/design/base/GaiaStatusChip.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import { gaiaNeutral, gaiaStatus } from '@/components/design/themes/tokens';

const props = withDefaults(
  defineProps<{
    signatureInfos?: VaultFileSignatureInfoInDb[] | null;
    compact?: boolean;
    /** Render as a labeled "Signed" tag (for inline placement next to a file name) instead of a bare icon. */
    labeled?: boolean;
  }>(),
  {
    signatureInfos: null,
    compact: false,
    labeled: false,
  }
);

const signatures = computed<VaultFileSignatureInfoInDb[]>(() => props.signatureInfos ?? []);
const hasSignatures = computed(() => signatures.value.length > 0);
const showCountBadge = computed(() => signatures.value.length > 1);
const iconSize = computed(() => (props.compact ? 16 : 18));

const successColor = gaiaStatus.success;
const surface = gaiaNeutral.surface;
</script>

<template>
  <span v-if="hasSignatures" class="rev-signature-cell" :class="{ labeled: props.labeled }">
    <gaia-tooltip placement="top">
      <template #trigger>
        <gaia-status-chip v-if="props.labeled" tone="success">
          {{ showCountBadge ? `Signed · ${signatures.length}` : 'Signed' }}
        </gaia-status-chip>
        <span v-else class="mark">
          <n-icon :component="IconCheckCircleFilled" :size="iconSize" :color="successColor" />
          <!-- More than one signature, so the count rides the mark rather than
               forcing a second column. -->
          <span v-if="showCountBadge" class="count">{{ signatures.length }}</span>
        </span>
      </template>

      <div class="detail">
        <gaia-signature-list :signatures="signatures" />
      </div>
    </gaia-tooltip>
  </span>
</template>

<style scoped>
.rev-signature-cell {
  display: inline-flex;
  align-items: center;
  line-height: 1;
}

/* Inline beside a file name, sized to sit with the other name-column badges. */
.rev-signature-cell.labeled {
  flex: none;
  margin-left: 6px;
}

.mark {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: default;
}

/* A ringed counter on the tick — the ring is what keeps it legible when the
   mark sits on a hovered (tinted) row. */
.count {
  position: absolute;
  right: -8px;
  bottom: -4px;
  box-sizing: border-box;
  min-width: 14px;
  height: 14px;
  padding: 0 4px;
  border: 1.5px solid v-bind(successColor);
  border-radius: 999px;
  background: v-bind(surface);
  color: v-bind(successColor);
  font-size: 9px;
  font-weight: 700;
  line-height: 11px;
  text-align: center;
}

.detail {
  min-width: 220px;
  max-width: 320px;
}
</style>
